"use client";

/**
 * useGlobalSearch — the one search rail, read over the socket. react-query keys
 * each query, so a superseded response is dropped without a generation guard.
 * The palette's tier one renders from local state; this is tier two.
 *
 * The debounce lives at the CALL SITE (see SEARCH_DEBOUNCE_MS), not in here, so
 * the caller decides when a keystroke costs a round trip.
 *
 * `keepPrevious` is deliberately OFF by default. A reader that renders ROWS must
 * never see a superseded query's answer, so it takes `undefined` while the next
 * one is in flight. Only a reader whose output may honestly lag one response —
 * the palette's tab strip, which would otherwise blink out on every keystroke —
 * opts in, and two readers of the same key share one round trip.
 */

import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { getSocket } from "@/stores/socketService";
import type { GlobalSearchResponse, SearchType } from "@bash/shared";

const EMPTY: GlobalSearchResponse = { top: [], groups: [] };

/** What a caller should debounce a typed query by before handing it over. */
export const SEARCH_DEBOUNCE_MS = 120;

/** Per-group page the rail returns when the caller does not ask for more. */
const DEFAULT_LIMIT = 5;

function run(
  q: string,
  types?: SearchType[],
  limit = DEFAULT_LIMIT
): Promise<GlobalSearchResponse> {
  return new Promise((resolve) => {
    const socket = getSocket();
    if (!socket?.connected) {
      // No socket (cold open, tests): settle as an empty result rather than
      // hanging. Tier one has already answered; this rail is the extra reach.
      resolve(EMPTY);
      return;
    }
    socket.emit(
      "search:global",
      { q, types, limit },
      (res: { success: boolean } & Partial<GlobalSearchResponse>) => {
        resolve(
          res.success ? { top: res.top ?? [], groups: res.groups ?? [] } : EMPTY
        );
      }
    );
  });
}

export function useGlobalSearch(
  query: string,
  types?: SearchType[],
  limit = DEFAULT_LIMIT,
  { keepPrevious = false }: { keepPrevious?: boolean } = {}
): { data: GlobalSearchResponse | undefined; isFetching: boolean } {
  const q = query.trim();
  const enabled =
    q.length > 0 && process.env.NEXT_PUBLIC_GLOBAL_SEARCH_V1 === "true";
  const result = useQuery({
    queryKey: ["global-search", q, types ?? null, limit],
    queryFn: () => run(q, types, limit),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
    staleTime: 30_000,
    gcTime: 60_000,
  });
  return { data: result.data, isFetching: result.isFetching };
}
